import { Fragment, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { DayCityPicker } from "./day-city-picker";
import { resolveStopTimeLabel } from "./trip-date";
import { legDirectionsUrl, tightLeg, travelLeg } from "../../lib/trip-travel";
import { StayPickerDialog, TripStayCard } from "./trip-stay-card";
import { useTripStayPrices } from "../../hooks/use-trip-stays";
import type {
  DiscoverItem,
  DiscoverSegment,
  Trip,
  TripStopSnapshot,
} from "../../types";
import { DiscoverContext } from "../../state/discover-context";
import { formatNumber } from "../../internal/format";
import {
  coordsOf,
  distanceParts,
  haversineMeters,
  walkMinutes,
  walkingDirectionsUrl,
  type LatLon,
} from "./trip-distance";
import { TripStopTimeEditor } from "./trip-stop-time-editor";
import { TripStopNoteEditor } from "./trip-stop-note-editor";
import { formatDayDateShort, tripStartOf } from "./trip-date";
import { StopPrice } from "./stop-price";
import { TripStopActions } from "./trip-stop-actions";
import { TripDayBooking } from "./trip-day-booking";
import type { OfferLookup } from "../../lib/trip-booking";

import { Icon } from "@majourneys/icons";
interface TripTimelineViewProps {
  readonly trip: Trip;
  readonly resolveItem: (itemId: string) => DiscoverItem | null;
  readonly onViewStop: (itemId: string) => void;
  /** When true, render per-stop reorder/remove controls + a per-day add button. */
  readonly editable?: boolean;
  readonly onAddStop?: (dayId: string) => void;
  readonly onRemoveStop?: (dayId: string, stopId: string) => void;
  readonly onMoveStop?: (
    dayId: string,
    stopId: string,
    direction: "up" | "down",
  ) => void;
  readonly onUpdateStopTime?: (
    dayId: string,
    stopId: string,
    startTime: string | undefined,
    endTime: string | undefined,
  ) => void;
  readonly onEditStopNote?: (
    dayId: string,
    stopId: string,
    note: string | undefined,
  ) => void;
  /**
   * Show only this day. The trip stays whole so each day keeps its own number
   * and date: filtering the trip down to one day used to relabel day 3 as
   * "Day 1" with day 1's date.
   */
  readonly onlyDayId?: string | null;
  /** Packages behind the trip's activities, for the day's booking block. */
  readonly offerFor?: OfferLookup;
  /** Open the "when and who" editor (the booking block's change link). */
  readonly onChangeWhen?: () => void;
}

/** Closer than this, two stops share a door: no walking line between them. */
const MIN_WALK_M = 50;

/** Colour + icon for a stop's category pill, keyed off its segment. */
const SEGMENT_PILL: Record<DiscoverSegment, { mod: string; icon: string }> = {
  restaurants: { mod: "is-food", icon: "ri-goblet-line" },
  locations: { mod: "is-place", icon: "ri-map-pin-line" },
  activities: { mod: "is-place", icon: "ri-map-pin-line" },
  bookable: { mod: "is-place", icon: "ri-map-pin-line" },
  stays: { mod: "is-stay", icon: "ri-hotel-line" },
  spas: { mod: "is-spa", icon: "ri-flower-line" },
  guides: { mod: "is-guide", icon: "ri-compass-3-line" },
  golf: { mod: "is-golf", icon: "ri-golf-ball-line" },
  nightlife: { mod: "is-nightlife", icon: "ri-goblet-line" },
  "beach-clubs": { mod: "is-beach-club", icon: "ri-umbrella-line" },
};

function pillFor(item: DiscoverItem): { mod: string; icon: string } {
  return SEGMENT_PILL[item.segment] ?? { mod: "is-place", icon: "ri-map-pin-line" };
}

/**
 * One row of the itinerary, and what we are able to say about it.
 *
 * A trip stores venue ids, not venues, so a stop only knows what it is once the
 * catalogue has arrived. Three states, not two: a stop we cannot confirm but
 * did write a name down for keeps its name and says only what is true — that
 * it cannot be opened right now. Announcing a place as gone is reserved for a
 * stop we never wrote anything down about.
 */
interface TimelineStop {
  readonly stopId: string;
  readonly startTime: string | undefined;
  readonly endTime: string | undefined;
  readonly note: string | undefined;
  /** The live catalogue row. Null while loading, and when the venue is gone. */
  readonly item: DiscoverItem | null;
  /** What the venue was called when it was saved. Only read when `item` is null. */
  readonly snapshot: TripStopSnapshot | null;
  /** Not here *yet*, as opposed to not here at all. */
  readonly pending: boolean;
}

/** The stop's own coordinates, when there is a resolved venue to take them from. */
function stopCoords(stop: TimelineStop | undefined): LatLon | null {
  return stop?.item ? coordsOf(stop.item) : null;
}

/**
 * Roamy-style itinerary timeline. Each day is a section with a summary
 * header (day label · estimated walking distance · spot count), then a
 * numbered dashed rail of stop cards. Between consecutive stops that
 * both have coordinates we show an estimated walk segment (haversine
 * distance + flat-speed time) and a one-tap walking-directions button.
 */
export function TripTimelineView({
  trip,
  resolveItem,
  onViewStop,
  editable = false,
  onAddStop,
  onRemoveStop,
  onMoveStop,
  onUpdateStopTime,
  onEditStopNote,
  onlyDayId = null,
  offerFor,
  onChangeWhen,
}: TripTimelineViewProps) {
  const { t, i18n } = useTranslation();
  /** Which stop's time or note editor the ⋯ menu opened, if any. */
  const [editing, setEditing] = useState<{ stopId: string; what: "time" | "note" } | null>(null);
  /**
   * Which days are folded, by id. A fold is how the guest is reading the trip
   * right now, not part of it, so it is not saved (the app's day list works
   * the same way).
   */
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set());
  const toggleFold = (dayId: string) =>
    setFolded((prev) => {
      const next = new Set(prev);
      if (next.has(dayId)) next.delete(dayId);
      else next.add(dayId);
      return next;
    });
  /** The city whose stay the picker is choosing, by its first day. */
  const [stayFor, setStayFor] = useState<string | null>(null);
  const stays = useTripStayPrices(trip);
  const lang = i18n.language;

  const distanceLabel = (meters: number): string => {
    const { value, unit } = distanceParts(meters);
    return t(`trip.timeline.distance_${unit}`, {
      value: formatNumber(value, lang),
    });
  };
  const minutesLabel = (meters: number): string =>
    t("trip.timeline.minutes_short", {
      value: formatNumber(walkMinutes(meters), lang),
    });

  // Whether there is a catalogue to judge the stops against. A request that
  // *failed* is neither pending nor loaded: treating it as pending shimmers
  // forever, treating it as loaded deletes the trip on screen.
  const discover = useContext(DiscoverContext);
  const catalogueLoading = discover?.isLoadingItems ?? false;
  const catalogueFailed = discover !== null && discover.itemsError !== null;

  // Resolve each day's stops. Nothing is dropped: a stop the catalogue cannot
  // answer for is still something the traveller put in the day, and silently
  // removing it is how a dead index used to render as an empty trip.
  const allDays = trip.days.map((day, dayIndex) => {
    const stops: TimelineStop[] = day.stops.map((stop) => {
      const item = resolveItem(stop.itemId);
      return {
        stopId: stop.id,
        startTime: stop.startTime,
        endTime: stop.endTime,
        note: stop.note,
        item,
        snapshot: item ? null : stop.snapshot ?? null,
        // A failed catalogue counts as "still coming" here on purpose. We do
        // not know that the venue is gone, only that we could not ask — and
        // the notice above the days is what says so, once, instead of every
        // row calling the place deleted.
        pending: !item && (catalogueLoading || catalogueFailed),
      };
    });
    return { day, dayIndex, stops };
  });

  // In editable mode (mobile Trips) keep empty days so each still renders its
  // "Add a stop" button — otherwise a brand-new trip is a dead end. In
  // read-only contexts (desktop Timeline tab) drop empty days for a tight view.
  const shown = onlyDayId ? allDays.filter((group) => group.day.id === onlyDayId) : allDays;
  const days = editable ? shown : shown.filter((group) => group.stops.length > 0);

  /**
   * The catalogue is down, not the trip.
   *
   * Without this the rows just sit there unnamed, which is right for a slow
   * connection and a lie for a dead one. This is the only place that says which
   * it is, and the only way to try again without leaving the trip.
   */
  const catalogueNotice = catalogueFailed ? (
    <div className="mj-trip-timeline__offline" role="status">
      <span>
        {t("trip.catalogue_failed", {
          defaultValue: "We could not load the places in this trip.",
        })}
      </span>
      <button
        type="button"
        className="mj-trip-timeline__offline-retry"
        // The catalogue is fetched once when the Discover app mounts and has no
        // second door to knock on, so trying again means loading the page
        // again. Honest, and better than a button that only looks like one.
        onClick={() => window.location.reload()}
      >
        {t("trip.catalogue_failed_retry", { defaultValue: "Try again" })}
      </button>
    </div>
  ) : null;

  if (days.length === 0) {
    return (
      <>
        {catalogueNotice}
        <div className="mj-trip-pane-empty">
          <Icon name="ri-route-line" aria-hidden="true" />
          <p>{t("trip.timeline.empty")}</p>
        </div>
      </>
    );
  }

  return (
    <div className="mj-trip-timeline">
      {catalogueNotice}
      {days.map(({ day, dayIndex, stops }) => {
        // Sum the day's walkable segments for the header summary. Only stops
        // the catalogue resolved have coordinates, so a stop it could not
        // answer for breaks the chain rather than guessing a leg.
        let dayMeters = 0;
        for (let i = 1; i < stops.length; i += 1) {
          const a = stopCoords(stops[i - 1]);
          const b = stopCoords(stops[i]);
          if (a && b) dayMeters += haversineMeters(a, b);
        }

        const isFolded = folded.has(day.id);
        // "10:00 to 20:00", from the stops' own times ("HH:MM" sorts as text).
        const times = day.stops
          .map((st) => st.startTime)
          .filter((v): v is string => typeof v === "string" && v.length > 0)
          .sort();
        const daySpan =
          times.length === 0
            ? null
            : times.length === 1 || times[0] === times[times.length - 1]
              ? times[0]!
              : t("trip.when.range", { start: times[0], end: times[times.length - 1] });

        return (
          <Fragment key={day.id}>
          {(() => {
            // Where you sleep, at the start of each city's days.
            const at = stays.legs.findIndex((l) => l.dayIds[0] === day.id);
            const leg = stays.legs[at];
            if (!leg || leg.nights < 1) return null;
            return (
              <TripStayCard
                trip={trip}
                leg={leg}
                price={stays.prices[at]!}
                editable={editable}
                onChoose={() => setStayFor(day.id)}
                onOpenVenue={onViewStop}
              />
            );
          })()}
          <section className="mj-trip-timeline__day">
            <header className="mj-trip-timeline__summary">
              <span className="mj-trip-timeline__summary-heading">
                <span className="mj-trip-timeline__summary-city">
                  {t("trip.day_label", { number: dayIndex + 1 })}
                </span>
                <span className="mj-trip-timeline__summary-date">
                  {formatDayDateShort(tripStartOf(trip), dayIndex, lang)}
                </span>
                <DayCityPicker trip={trip} day={day} dayIndex={dayIndex} editable={editable} />
              </span>
              <button
                type="button"
                className="mj-trip-timeline__summary-meta mj-trip-timeline__fold"
                onClick={() => toggleFold(day.id)}
                aria-expanded={!isFolded}
                aria-label={t(isFolded ? "trip.expand_day" : "trip.collapse_day", {
                  day: t("trip.day_label", { number: dayIndex + 1 }),
                })}
              >
                <span>
                  {dayMeters > 0 && <>{distanceLabel(dayMeters)} · </>}
                  {t("trip.timeline.spots", {
                    count: stops.length,
                    formattedCount: formatNumber(stops.length, lang),
                  })}
                </span>
                <Icon
                  name={isFolded ? "ri-arrow-down-s-line" : "ri-arrow-up-s-line"}
                  className="mj-trip-timeline__fold-icon"
                  aria-hidden="true"
                />
              </button>
            </header>

            {isFolded ? (
              // Folded: one line, the day's hours if it has any. Tapping it
              // opens the day again.
              daySpan ? (
                <button type="button" className="mj-trip-timeline__folded" onClick={() => toggleFold(day.id)}>
                  {daySpan}
                </button>
              ) : null
            ) : (
            <>
            <ol className="mj-trip-timeline__list">
              {stops.map((entry, index) => {
                const { stopId, startTime, endTime, note, item, snapshot } = entry;
                const here = stopCoords(entry);
                const there = stopCoords(stops[index + 1]);
                const meters = here && there ? haversineMeters(here, there) : 0;
                // Two stops at one address ("1 min · 0 m") say nothing: no walk line.
                const segment: { from: LatLon; to: LatLon; meters: number } | null =
                  here && there && meters >= MIN_WALK_M ? { from: here, to: there, meters } : null;
                // On foot or by car, and whether the day leaves time for it.
                const leg = segment ? travelLeg(segment.from, segment.to) : null;
                const nextEntry = stops[index + 1];
                const nextStop = nextEntry ? day.stops.find((st) => st.id === nextEntry.stopId) : undefined;
                const thisStop = day.stops.find((st) => st.id === stopId);
                const tight =
                  leg && thisStop && nextStop ? tightLeg(thisStop, index, nextStop, index + 1, leg) : null;
                const nextName =
                  nextEntry?.item?.title ?? nextEntry?.snapshot?.title ?? t("trip.travel.next_stop");
                // The name we can stand behind: the catalogue's, then the one
                // written down when the stop was saved, then nothing.
                const title = item?.title ?? snapshot?.title ?? null;
                const image = item?.image ?? snapshot?.image ?? null;

                return (
                  <li className="mj-trip-timeline__item" key={stopId}>
                    <div className="mj-trip-timeline__node">
                      <span className="mj-trip-timeline__num">{index + 1}</span>
                    </div>
                    <div className="mj-trip-timeline__stop">
                      <div className="mj-trip-timeline__row">
                        {item ? (
                          <button
                            type="button"
                            className="mj-trip-timeline__card"
                            onClick={() => onViewStop(item.id)}
                            aria-label={t("trip.timeline.view_stop", {
                              title: item.title,
                            })}
                          >
                            <span
                              className="mj-trip-timeline__thumb"
                              style={{ backgroundImage: `url(${item.image})` }}
                              aria-hidden="true"
                            />
                            <span className="mj-trip-timeline__info">
                              <strong>{item.title}</strong>
                              {(item.subtitle || item.pinLabel) && (
                                <span
                                  className={`mj-trip-timeline__pill ${pillFor(item).mod}`}
                                >
                                  <Icon
                                    name={pillFor(item).icon}
                                    aria-hidden="true"
                                  />
                                  {item.subtitle || item.pinLabel}
                                </span>
                              )}
                              {/* Time and price on one line, in the card (rail B,
                                  owner 2026-11-03): the ⋯ menu edits the time. */}
                              <span className="mj-trip-timeline__meta">
                                <span>{resolveStopTimeLabel({ startTime, endTime }, index, t)}</span>
                                <StopPrice item={item} className="mj-trip-timeline__meta-price" />
                              </span>
                            </span>
                          </button>
                        ) : (
                          // No live row behind this one, so there is nothing to
                          // open: the same card, without the press. What it says
                          // underneath is the honest difference between a stop
                          // still arriving, one we can still name, and one we
                          // never wrote anything down about.
                          <div
                            className="mj-trip-timeline__card mj-trip-timeline__card--flat"
                            aria-busy={catalogueLoading || undefined}
                          >
                            <span
                              className={`mj-trip-timeline__thumb${image ? "" : " mj-trip-timeline__thumb--empty"}`}
                              style={
                                image ? { backgroundImage: `url(${image})` } : undefined
                              }
                              aria-hidden="true"
                            />
                            <span className="mj-trip-timeline__info">
                              <strong>
                                {title ??
                                  (entry.pending
                                    ? t("trip.stop_loading", {
                                        defaultValue: "Loading this stop",
                                      })
                                    : t("trip.stop_unavailable", {
                                        defaultValue: "This place is no longer available",
                                      }))}
                              </strong>
                              {!entry.pending && title && (
                                <span className="mj-trip-timeline__pill">
                                  <Icon
                                    name="ri-error-warning-line"
                                    aria-hidden="true"
                                  />
                                  {t("trip.stop_unlisted", {
                                    defaultValue: "No longer listed",
                                  })}
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                        {editable && (
                          <TripStopActions
                            title={
                              title ??
                              t("trip.stop_unavailable", {
                                defaultValue: "This place is no longer available",
                              })
                            }
                            hasNote={!!note}
                            canMoveUp={index > 0}
                            canMoveDown={index < stops.length - 1}
                            onEditTime={
                              onUpdateStopTime
                                ? () => setEditing({ stopId, what: "time" })
                                : undefined
                            }
                            onEditNote={
                              onEditStopNote
                                ? () => setEditing({ stopId, what: "note" })
                                : undefined
                            }
                            onMoveUp={() => onMoveStop?.(day.id, stopId, "up")}
                            onMoveDown={() => onMoveStop?.(day.id, stopId, "down")}
                            onRemove={() => onRemoveStop?.(day.id, stopId)}
                          />
                        )}
                      </div>

                      {note && (
                        <p className="mj-trip-timeline__note">
                          <Icon name="ri-sticky-note-line" aria-hidden="true" />
                          {note}
                        </p>
                      )}

                      {editable && (
                        <div className="mj-trip-timeline__edit">
                          {onUpdateStopTime && (
                            <TripStopTimeEditor
                              hideTrigger={Boolean(item)}
                              stopIndex={index}
                              startTime={startTime}
                              endTime={endTime}
                              open={editing?.stopId === stopId && editing.what === "time"}
                              onOpenChange={(o) =>
                                setEditing(o ? { stopId, what: "time" } : null)
                              }
                              onSave={(s, e) =>
                                onUpdateStopTime(day.id, stopId, s, e)
                              }
                            />
                          )}
                          {onEditStopNote && (
                            <TripStopNoteEditor
                              note={note}
                              hideTrigger
                              open={editing?.stopId === stopId && editing.what === "note"}
                              onOpenChange={(o) =>
                                setEditing(o ? { stopId, what: "note" } : null)
                              }
                              onSave={(n) => onEditStopNote(day.id, stopId, n)}
                            />
                          )}
                        </div>
                      )}

                      {segment && (
                        <div className={`mj-trip-timeline__leg${tight ? " is-tight" : ""}`}>
                          <span className="mj-trip-timeline__leg-pill">
                            <Icon
                              name={leg?.kind === "drive" ? "ri-car-line" : "ri-walk-line"}
                              aria-hidden="true"
                            />
                            {leg
                              ? t("trip.timeline.minutes_short", { value: formatNumber(leg.minutes, lang) })
                              : minutesLabel(segment.meters)}
                          </span>
                          <span className="mj-trip-timeline__leg-dist">{distanceLabel(segment.meters)}</span>
                          <a
                            className="mj-trip-timeline__leg-dir"
                            href={legDirectionsUrl(segment.from, segment.to, leg?.kind ?? "walk")}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t(
                              leg?.kind === "drive" ? "trip.travel.directions_drive" : "trip.timeline.directions",
                            )}
                          >
                            <Icon name="ri-arrow-right-up-line" aria-hidden="true" />
                          </a>
                        </div>
                      )}
                      {segment && leg && tight && (
                        <div className="mj-trip-timeline__tight" role="note">
                          <span>
                            {t(leg.kind === "drive" ? "trip.travel.tight_drive" : "trip.travel.tight_walk", {
                              name: nextName,
                              minutes: formatNumber(leg.minutes, lang),
                            })}
                          </span>
                          {editable && onUpdateStopTime && nextStop ? (
                            <button
                              type="button"
                              onClick={() => onUpdateStopTime(day.id, nextStop.id, tight.start, tight.end)}
                            >
                              {t("trip.travel.fix", { time: tight.start })}
                            </button>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
            {editable && (
              <button
                type="button"
                className="mj-trip-timeline__add-stop"
                onClick={() => onAddStop?.(day.id)}
              >
                <Icon name="ri-add-line" aria-hidden="true" />
                {t("trip.add_stop")}
              </button>
            )}
            {editable && onChangeWhen && (
              <TripDayBooking
                trip={trip}
                day={day}
                dayIndex={dayIndex}
                resolveItem={resolveItem}
                offerFor={offerFor}
                onChangeWhen={onChangeWhen}
              />
            )}
            </>
            )}
          </section>
          </Fragment>
        );
      })}
      {(() => {
        const leg = stayFor ? stays.legs.find((l) => l.dayIds[0] === stayFor) : null;
        return leg ? <StayPickerDialog trip={trip} leg={leg} onClose={() => setStayFor(null)} /> : null;
      })()}
    </div>
  );
}
